import { Alert, Col, Collapse, Divider, Modal, Row } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { NavLink } from 'react-router-dom';
import useUnmount from 'common/hooks/useUnmount';

import { Button } from 'common/components';
import { SignatureForm } from 'common/forms';
import { SignatureFlag, SignatureScope } from 'common/models/Transaction';
import DocumentActions from 'features/form/components/DocumentActions';
import { DocumentSignaturesFormValues } from 'common/models/Forms';
import { TokenState } from 'common/api/token';
import { I18nNameSpaces, Languages } from 'common/services/i18n';
import { AppState } from 'common/models/AppState';
import { PdfRenderer } from 'common/pdf/pdf-renderer';

import {
  forwardDocumentsAction,
  requestSignatureCodeAction,
  resetSignaturesStateAction,
  selectClientSignature,
  setDocumentUploadStatusAction,
  setSelectedDocumentsAction,
  submitSignatureCodeAction,
  UploadState,
} from './ducks';

import {
  InvestingStrategy,
  selectFormSignatures,
  selectInformationsCustomerInfo1,
  selectIsCustomerInfo2Visible,
  selectIsFormVerified,
  selectStrategy,
} from '../../ducks';
import { useIsMobileSm } from 'common/hooks/useIsMobile';
import { setVerificationLogVisibleAction } from './features/verification-log/ducks';
import VerificationLogFeature from './features/verification-log';
import { getSignatureBlockReason } from './signatureBlockReason';
import { buildFormStepFailuresContent } from '../../validation/formStepFailuresContent';
import { getFormSubmissionValidation } from '../../validation/formSubmissionValidation';

export default function TransactionFeature() {
  const { t: tCommon } = useTranslation();
  const {
    t,
    i18n: { language },
  } = useTranslation(I18nNameSpaces.Forms);
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const isMobile = useIsMobileSm();
  const pdfRenderer = new PdfRenderer(store.getState());

  const customerInfo1 = useSelector(selectInformationsCustomerInfo1);
  const isCustomerInfo2Visible = useSelector(selectIsCustomerInfo2Visible);
  const strategy = useSelector(selectStrategy);
  const isConservative = strategy.type === InvestingStrategy.Conservative;
  const clientSignatures = useSelector(selectClientSignature);
  const existingSignatures = useSelector(selectFormSignatures);
  const signedDocumentIds = (Object.keys(existingSignatures) as SignatureScope[]).filter(
    (documentId) => existingSignatures[documentId] === SignatureFlag.SIGNED,
  );
  const hasAnyValidSignature = signedDocumentIds.length > 0;

  const { isVerifiedEmail, isVerifiedPhone } = useSelector(selectIsFormVerified);
  const isContactVerified = isVerifiedPhone && isVerifiedEmail;
  const hasCustomerName = Boolean(customerInfo1?.firstName) && Boolean(customerInfo1?.lastName);
  const signatureBlockReason = getSignatureBlockReason(isContactVerified, hasCustomerName);
  const isFullyVerified = signatureBlockReason === null;
  const formSubmissionValidation = useSelector(getFormSubmissionValidation);
  const isFormReadyForSubmission = formSubmissionValidation.isValid;

  useUnmount(() => {
    dispatch(resetSignaturesStateAction());
  });

  function handleRequestCode() {
    dispatch(requestSignatureCodeAction());
  }

  function handleVerifyCode(code: DocumentSignaturesFormValues['code']) {
    dispatch(submitSignatureCodeAction(code));
  }

  function handleDocumentSelect(key: SignatureScope, isSelected: boolean) {
    dispatch(setSelectedDocumentsAction({ document: key, isSelected }));
  }

  function handleShowVerificationLog() {
    dispatch(setVerificationLogVisibleAction(true));
  }

  function handleResendDocuments() {
    if (!isFormReadyForSubmission) {
      return;
    }
    dispatch(setDocumentUploadStatusAction(UploadState.UPLOADING));
    dispatch(forwardDocumentsAction(signedDocumentIds));
  }

  function getErrorMessage() {
    if (signatureBlockReason === 'contact') {
      return (
        <Trans
          t={tCommon}
          i18nKey="signatureForm.readOnlyWarning"
          components={[<NavLink to="../verification" key={0} />]}
        />
      );
    }

    if (signatureBlockReason === 'customerName') {
      return (
        <Trans
          t={tCommon}
          i18nKey="signatureForm.readOnlyWarningCustomerName"
          components={[<NavLink to="../informations/customer-info-1" key={0} />]}
        />
      );
    }

    if (clientSignatures.status === TokenState.DELIVERY_ERROR) {
      return t([`signatureForm.request.${clientSignatures.httpStatus}`, 'signatureForm.request.error']);
    }

    if (clientSignatures.status === TokenState.SUBMISSION_ERROR) {
      return clientSignatures.attemptsLeft
        ? t(`signatureForm.sign.errorAttempts`, {
            attemptsLeft: clientSignatures.attemptsLeft,
          })
        : t([`signatureForm.sign.${clientSignatures.httpStatus}`, 'signatureForm.sign.error']);
    }

    return null;
  }

  function getSuccessMessage() {
    if (clientSignatures.status === TokenState.DELIVERED) {
      return t(`signatureForm.request.success`);
    }
    if (clientSignatures.status === TokenState.VERIFIED) {
      return t(`signatureForm.sign.success`);
    }

    return null;
  }

  function renderDocumentActions(documentId: SignatureScope) {
    return (
      isFullyVerified &&
      pdfRenderer.isRenderable(documentId) && <DocumentActions id={documentId} onChange={handleDocumentSelect} />
    );
  }

  function renderDocumentItem(documentId: SignatureScope) {
    return {
      key: documentId,
      label: t(`signatureForm.fileTypes.${documentId}`),
      extra: renderDocumentActions(documentId),
      children: pdfRenderer.isRenderable(documentId) ? (
        <PDFDownloadLink
          document={pdfRenderer.renderDocument(documentId)}
          fileName={t(`signatureForm.fileNames.${documentId}`)}
        >
          {({ loading }: { loading: boolean }) => (
            <Button block disabled={loading} loading={loading}>
              {t(`signatureForm.fileTypes.${documentId}`)}
            </Button>
          )}
        </PDFDownloadLink>
      ) : (
        t('signatureForm.fileTypes.valuesMissing')
      ),
    };
  }

  return (
    <div className="transaction-feature" style={{ margin: '30px 0' }}>
      <VerificationLogFeature />
      <Modal
        centered
        destroyOnHidden
        title={t('signatureForm.uploadInProgress')}
        open={clientSignatures.uploadStatus === UploadState.UPLOADING}
        footer={null}
        closable={false}
      >
        <p className="transaction-upload-spinner">
          <LoadingOutlined />
        </p>
      </Modal>
      {isFormReadyForSubmission ? null : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          title={t('form.finalValidationBlocked')}
          description={buildFormStepFailuresContent(formSubmissionValidation.failures, t)}
        />
      )}

      <Collapse
        accordion
        destroyOnHidden
        items={[
          renderDocumentItem(SignatureScope.CUSTOMER_INFO_1),
          ...(isCustomerInfo2Visible ? [renderDocumentItem(SignatureScope.CUSTOMER_INFO_2)] : []),
          renderDocumentItem(SignatureScope.INVESTING),
          renderDocumentItem(SignatureScope.GENERAL_AGREEMENT),
          renderDocumentItem(SignatureScope.PROTOCOL),
          renderDocumentItem(SignatureScope.AGREEMENT),
          renderDocumentItem(SignatureScope.MEETING),
        ]}
      />

      <Row style={{ marginTop: '30px' }} gutter={30}>
        <Col sm={24} lg={16}>
          {isFullyVerified ? (
            <>
              <p className="documents-hint">{t('signatureForm.documentSelectionHint')}</p>
              <p className="documents-hint">{t('signatureForm.documentForwardingHint')}</p>
            </>
          ) : null}
        </Col>
        <Col sm={24} lg={8}>
          <Row className="global-actions">
            <Col sm={24} lg={isConservative ? 24 : 12}>
              <Button block={isMobile} onClick={handleShowVerificationLog}>
                {t('signatureForm.verificationLog.title')}
              </Button>
            </Col>
            {/* {!isConservative && ( */}
            {/*    <Col sm={24} lg={12}> */}
            {/*        <Button type="primary" block={isMobile}> */}
            {/*            <NavLink to="BoT"> */}
            {/*                {t('signatureForm.createBoTAccount')} */}
            {/*            </NavLink> */}
            {/*        </Button> */}
            {/*    </Col> */}
            {/* )} */}
          </Row>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <SignatureForm
            signature={hasAnyValidSignature ? pdfRenderer.createClientSignature() : null}
            readOnly={!isFullyVerified}
            onRequestCode={handleRequestCode}
            onVerifyCode={handleVerifyCode}
            error={getErrorMessage()}
            success={getSuccessMessage()}
            loadingRequest={clientSignatures.status === TokenState.REQUESTED}
            disableRequest={clientSignatures.selectedDocuments.length === 0 || !isFormReadyForSubmission}
            loadingVerify={clientSignatures.status === TokenState.SUBMITTED}
            disableVerify={
              !isFormReadyForSubmission ||
              [TokenState.DEFAULT, TokenState.DELIVERY_ERROR, TokenState.REQUESTED].includes(clientSignatures.status)
            }
          />
        </Col>

        {isFullyVerified && hasAnyValidSignature ? (
          <Col span={24} style={{ marginTop: '16px' }}>
            <Button block disabled={!isFormReadyForSubmission} onClick={handleResendDocuments}>
              {t('signatureForm.resend.title')}
            </Button>
          </Col>
        ) : null}
      </Row>

      <Divider />

      <Row>
        <p className="documents-hint highlighted">
          <Trans
            t={t}
            i18nKey="signatureForm.identityHint"
            components={[
              <a
                href={
                  language === Languages.Slovak ? 'mailto:sk@phoenix-investor.com' : 'mailto:cz@phoenix-investor.com'
                }
                key={0}
              />,
            ]}
          />
        </p>
      </Row>
    </div>
  );
}
